<template>
  <div class="login-container">
    <!-- 背景动画 -->
    <div class="bg-animation">
      <div class="gradient-bg"></div>
      <div class="particles" ref="particlesContainer"></div>
    </div>
    
    <!-- 登录卡片 -->
    <div class="login-card">
      <div class="card-header">
        <div class="logo-icon">
          <Icon icon="carbon:ai-status" width="48" height="48" />
        </div>
        <h2 class="card-title">{{ isRenew ? '账号续费' : isRegister ? '商户账号注册' : '商户账号登录' }}</h2>
        <p class="card-subtitle">{{ isRenew ? '输入激活码续费您的账号' : isRegister ? '填写信息完成注册' : '欢迎回来，请登录您的账号' }}</p>
      </div>

      <!-- 登录表单 -->
      <el-form v-if="!isRegister && !isRenew" :model="loginForm" ref="loginFormRef" :rules="rules">
        <el-form-item prop="username">
          <el-input v-model="loginForm.username" placeholder="账号" size="large">
            <template #prefix>
              <Icon icon="heroicons:user" width="18" height="18" />
            </template>
          </el-input>
        </el-form-item>

        <el-form-item prop="password">
          <el-input v-model="loginForm.password" type="password" placeholder="密码" show-password size="large">
            <template #prefix>
              <Icon icon="heroicons:lock-closed" width="18" height="18" />
            </template>
          </el-input>
        </el-form-item>

        <el-form-item>
          <el-button type="primary" @click="handleLogin" :loading="loading" class="login-btn" size="large">
            {{ loading ? '登录中...' : '立即登录' }}
          </el-button>
        </el-form-item>
      </el-form>

      <!-- 注册表单 -->
      <el-form v-else-if="isRegister" :model="registerForm" ref="registerFormRef" :rules="registerRules">
        <el-form-item prop="account">
          <el-input v-model="registerForm.account" placeholder="账号" size="large">
            <template #prefix>
              <Icon icon="heroicons:user" width="18" height="18" />
            </template>
          </el-input>
        </el-form-item>

        <el-form-item prop="mobile">
          <el-input v-model="registerForm.mobile" placeholder="手机号" size="large">
            <template #prefix>
              <Icon icon="heroicons:device-phone-mobile" width="18" height="18" />
            </template>
          </el-input>
        </el-form-item>

        <el-form-item prop="password">
          <el-input v-model="registerForm.password" type="password" placeholder="密码" show-password size="large">
            <template #prefix>
              <Icon icon="heroicons:lock-closed" width="18" height="18" />
            </template>
          </el-input>
        </el-form-item>

        <el-form-item prop="confirmPassword">
          <el-input v-model="registerForm.confirmPassword" type="password" placeholder="确认密码" show-password size="large">
            <template #prefix>
              <Icon icon="heroicons:lock-closed" width="18" height="18" />
            </template>
          </el-input>
        </el-form-item>

        <el-form-item prop="registerCode">
          <el-input v-model="registerForm.registerCode" placeholder="注册码" size="large">
            <template #prefix>
              <Icon icon="heroicons:key" width="18" height="18" />
            </template>
          </el-input>
        </el-form-item>

        <el-form-item>
          <el-button type="primary" @click="handleRegister" :loading="loading" class="login-btn" size="large">
            {{ loading ? '注册中...' : '立即注册' }}
          </el-button>
        </el-form-item>
      </el-form>

      <!-- 续费表单 -->
      <el-form v-else :model="renewForm" ref="renewFormRef" :rules="renewRules">
        <el-form-item prop="account">
          <el-input v-model="renewForm.account" placeholder="账号" size="large">
            <template #prefix>
              <Icon icon="heroicons:user" width="18" height="18" />
            </template>
          </el-input>
        </el-form-item>

        <el-form-item prop="activationCode">
          <el-input v-model="renewForm.activationCode" placeholder="激活码" size="large">
            <template #prefix>
              <Icon icon="heroicons:key" width="18" height="18" />
            </template>
          </el-input>
        </el-form-item>

        <el-form-item>
          <el-button type="primary" @click="handleRenew" :loading="loading" class="login-btn" size="large">
            {{ loading ? '续费中...' : '立即续费' }}
          </el-button>
        </el-form-item>
      </el-form>

      <!-- 底部链接 -->
      <div class="card-footer" v-if="!isRenew && !isRegister">
        <div class="footer-links">
          <a @click="switchToRenew">账号续费</a>
          <span class="divider">|</span>
          <a @click="switchToRegister">立即注册</a>
        </div>
      </div>
      
      <div class="card-footer" v-else>
        <a @click="switchToLogin" class="back-link">
          <Icon icon="heroicons:arrow-left" width="16" height="16" />
          返回登录
        </a>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import axios from 'axios'
import md5 from 'md5'
import Cookies from 'js-cookie'
import config from '@/config'
import { Icon } from '@iconify/vue'

export default {
  name: 'TheLogin',
  components: { Icon },
  setup() {
    const router = useRouter()
    const isRegister = ref(false)
    const isRenew = ref(false)
    const loading = ref(false)
    const particlesContainer = ref(null)
    
    const loginForm = ref({ username: '', password: '' })
    const registerForm = ref({ account: '', mobile: '', password: '', confirmPassword: '', registerCode: '' })
    const renewForm = ref({ account: '', activationCode: '' })
    
    const loginFormRef = ref(null)
    const registerFormRef = ref(null)
    const renewFormRef = ref(null)

    const rules = {
      username: [{ required: true, message: '请输入账号', trigger: 'blur' }],
      password: [{ required: true, message: '请输入密码', trigger: 'blur' }]
    }

    const renewRules = {
      account: [{ required: true, message: '请输入账号', trigger: 'blur' }],
      activationCode: [{ required: true, message: '请输入激活码', trigger: 'blur' }]
    }

    const validatePassword = (rule, value, callback) => {
      if (value !== registerForm.value.password) {
        callback(new Error('两次输入密码不一致'))
      } else {
        callback()
      }
    }

    const registerRules = {
      account: [
        { required: true, message: '请输入账号', trigger: 'blur' },
        { min: 6, message: '账号长度至少6位', trigger: 'blur' }
      ],
      mobile: [
        { required: true, message: '请输入手机号', trigger: 'blur' },
        { pattern: /^1[3-9]\d{9}$/, message: '手机号格式不正确', trigger: 'blur' }
      ],
      password: [
        { required: true, message: '请输入密码', trigger: 'blur' },
        { min: 6, message: '密码长度至少6位', trigger: 'blur' }
      ],
      confirmPassword: [
        { required: true, message: '请再次输入密码', trigger: 'blur' },
        { validator: validatePassword, trigger: 'blur' }
      ],
      registerCode: [{ required: true, message: '请输入注册码', trigger: 'blur' }]
    }

    const switchToRegister = () => {
      isRegister.value = true
      isRenew.value = false
    }

    const switchToRenew = () => {
      isRenew.value = true
      isRegister.value = false
    }

    const switchToLogin = () => {
      isRegister.value = false
      isRenew.value = false
    }

    const handleLogin = () => {
      loginFormRef.value.validate(async (valid) => {
        if (!valid) return
        loading.value = true
        try {
          const response = await axios.post(`${config.BASE_URL}/shop/api/login.php`, {
            account: loginForm.value.username,
            password: md5(loginForm.value.password)
          })
          if (response.data.code === 200) {
            Cookies.set('user_token', response.data.data.id, { expires: 7 })
            Cookies.set('user_info', JSON.stringify(response.data.data), { expires: 7 })
            ElMessage.success(response.data.message)
            router.push('/dashboard')
          } else {
            ElMessage.error(response.data.message || '登录失败')
          }
        } catch (error) {
          ElMessage.error('网络连接异常')
        } finally {
          loading.value = false
        }
      })
    }

    const handleRegister = () => {
      registerFormRef.value.validate(async (valid) => {
        if (!valid) return
        loading.value = true
        try {
          const response = await axios.post(`${config.BASE_URL}/shop/api/register.php`, {
            account: registerForm.value.account,
            mobile: registerForm.value.mobile,
            password: md5(registerForm.value.password),
            invitation_code: registerForm.value.registerCode
          })
          if (response.data.code === 200) {
            ElMessage.success('注册成功')
            isRegister.value = false
            registerForm.value = { account: '', mobile: '', password: '', confirmPassword: '', registerCode: '' }
          } else {
            ElMessage.error(response.data.message || '注册失败')
          }
        } catch (error) {
          ElMessage.error('网络连接异常')
        } finally {
          loading.value = false
        }
      })
    }

    const handleRenew = async () => {
      try {
        await renewFormRef.value.validate()
        loading.value = true
        const response = await axios.post(`${config.BASE_URL}/shop/api/recharge.php`, {
          account: renewForm.value.account,
          activation_code: renewForm.value.activationCode
        })
        if (response.data.code === 200) {
          ElMessage.success(response.data.message)
          renewForm.value = { account: '', activationCode: '' }
          isRenew.value = false
        } else {
          ElMessage.error(response.data.message || '续费失败')
        }
      } catch (error) {
        ElMessage.error('网络连接异常')
      } finally {
        loading.value = false
      }
    }

    // 粒子效果
    let particlesAnimation = null
    let particlesList = []

    const createParticles = () => {
      const container = particlesContainer.value
      if (!container) return
      
      container.innerHTML = ''
      particlesList = []
      
      const count = 80
      for (let i = 0; i < count; i++) {
        const particle = document.createElement('div')
        particle.classList.add('particle')
        const size = 2 + Math.random() * 4
        particle.style.width = `${size}px`
        particle.style.height = `${size}px`
        particle.style.left = `${Math.random() * 100}%`
        particle.style.top = `${Math.random() * 100}%`
        particle.style.animationDelay = `${Math.random() * 5}s`
        particle.style.animationDuration = `${15 + Math.random() * 10}s`
        particle.style.opacity = 0.3 + Math.random() * 0.5
        container.appendChild(particle)
        particlesList.push(particle)
      }
    }

    onMounted(() => {
      createParticles()
      window.addEventListener('resize', createParticles)
    })

    onUnmounted(() => {
      window.removeEventListener('resize', createParticles)
      if (particlesAnimation) {
        cancelAnimationFrame(particlesAnimation)
      }
    })

    return {
      isRegister,
      isRenew,
      loading,
      loginForm,
      registerForm,
      renewForm,
      rules,
      registerRules,
      renewRules,
      loginFormRef,
      registerFormRef,
      renewFormRef,
      particlesContainer,
      switchToRegister,
      switchToRenew,
      switchToLogin,
      handleLogin,
      handleRegister,
      handleRenew
    }
  }
}
</script>

<style scoped lang="scss">
.login-container {
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  overflow: hidden;
}

.bg-animation {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 0;
  
  .gradient-bg {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, #0f0c1f 0%, #1a1535 50%, #0d0b1a 100%);
  }
  
  .particles {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    overflow: hidden;
    
    .particle {
      position: absolute;
      background: linear-gradient(135deg, #9d4edd, #6546b4);
      border-radius: 50%;
      animation: floatParticle linear infinite;
      pointer-events: none;
    }
  }
}

@keyframes floatParticle {
  0% {
    transform: translateY(0) translateX(0);
    opacity: 0;
  }
  20% {
    opacity: 0.8;
  }
  80% {
    opacity: 0.8;
  }
  100% {
    transform: translateY(-100vh) translateX(50px);
    opacity: 0;
  }
}

.login-card {
  position: relative;
  z-index: 1;
  width: 460px;
  max-width: 90%;
  background: rgba(18, 14, 33, 0.9);
  backdrop-filter: blur(20px);
  border-radius: 32px;
  padding: 40px;
  border: 1px solid rgba(157, 78, 221, 0.3);
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 480px) {
    padding: 30px 24px;
  }
  
  .card-header {
    text-align: center;
    margin-bottom: 32px;
    
    .logo-icon {
      width: 80px;
      height: 80px;
      background: linear-gradient(135deg, #6546b4, #9d4edd);
      border-radius: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0 auto 20px;
      color: #fff;
      box-shadow: 0 10px 30px rgba(101, 70, 180, 0.4);
    }
    
    .card-title {
      font-size: 28px;
      font-weight: 700;
      background: linear-gradient(135deg, #fff, #c084fc);
      -webkit-background-clip: text;
      background-clip: text;
      color: transparent;
      margin-bottom: 8px;
    }
    
    .card-subtitle {
      font-size: 14px;
      color: #a0a0c0;
    }
  }
  
  :deep(.el-form-item) {
    margin-bottom: 24px;
  }
  
  :deep(.el-input__wrapper) {
    background: rgba(26, 21, 53, 0.8);
    border: 1px solid rgba(157, 78, 221, 0.3);
    border-radius: 14px;
    box-shadow: none;
    padding: 4px 16px;
    
    &:hover {
      border-color: rgba(157, 78, 221, 0.6);
    }
    
    &.is-focus {
      border-color: #9d4edd;
      box-shadow: 0 0 0 2px rgba(157, 78, 221, 0.2);
    }
    
    .el-input__prefix {
      color: #9d4edd;
      margin-right: 8px;
    }
    
    .el-input__inner {
      color: #fff;
      background: transparent;
      height: 48px;
      
      &::placeholder {
        color: #5a5a7a;
      }
    }
  }
  
  .login-btn {
    width: 100%;
    height: 48px;
    background: linear-gradient(135deg, #6546b4 0%, #9d4edd 100%);
    border: none;
    border-radius: 14px;
    font-size: 16px;
    font-weight: 600;
    margin-top: 8px;
    
    &:hover {
      background: linear-gradient(135deg, #7556c4 0%, #ad5eed 100%);
      transform: translateY(-2px);
      box-shadow: 0 8px 25px rgba(157, 78, 221, 0.4);
    }
  }
  
  .card-footer {
    margin-top: 24px;
    text-align: center;
    
    .footer-links {
      display: flex;
      justify-content: center;
      gap: 16px;
      
      a {
        color: #a0a0c0;
        font-size: 14px;
        cursor: pointer;
        transition: color 0.2s;
        
        &:hover {
          color: #c084fc;
        }
      }
      
      .divider {
        color: #3a3a5a;
      }
    }
    
    .back-link {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      color: #a0a0c0;
      font-size: 14px;
      cursor: pointer;
      transition: color 0.2s;
      
      &:hover {
        color: #c084fc;
      }
    }
  }
}
</style>